/**
 * Module-scoped state for the post-conversion inline notification.
 *
 * When a writer accepts the convert-to-series suggestion, we record the
 * conversion event here. The next assistant message (any message created
 * after the conversion timestamp) renders the notification once, then
 * the entry is marked shown so it never repeats.
 *
 * Lives outside React state intentionally — survives component re-renders
 * and prop drilling without an extra context.
 */

const pending = new Map(); // projectId -> { bookDiscoverySessionId, conversionTime, shownForMessageId }

export function setPendingConversion(projectId, bookDiscoverySessionId) {
  if (!projectId || !bookDiscoverySessionId) return;
  pending.set(projectId, {
    bookDiscoverySessionId,
    conversionTime: Date.now(),
    shownForMessageId: null,
  });
}

export function clearPendingConversion(projectId) {
  pending.delete(projectId);
}

/**
 * Should this assistant message render the inline post-conversion notification?
 * Returns the bookDiscoverySessionId if yes, otherwise null.
 *
 * The first assistant message created after the conversion event claims
 * the notification. Subsequent calls for the same message return the same
 * id; calls for other messages return null.
 */
export function shouldShowFor(projectId, messageId, messageCreatedAt) {
  if (!projectId || !messageId) return null;
  const entry = pending.get(projectId);
  if (!entry) return null;

  if (entry.shownForMessageId) {
    return entry.shownForMessageId === messageId ? entry.bookDiscoverySessionId : null;
  }

  const msgTime = messageCreatedAt ? new Date(messageCreatedAt).getTime() : Date.now();
  if (msgTime >= entry.conversionTime) {
    entry.shownForMessageId = messageId;
    return entry.bookDiscoverySessionId;
  }

  return null;
}
